/**
 * CustomerDetailPage — Detalhe/perfil do cliente (CRM).
 *
 * Anatomia (Design System — mockup `crm-customer-detail.html`):
 *  - Toolbar de página (3 zonas): breadcrumbs à esquerda (CRM › Clientes ›
 *    {nome}, nunca seta voltar), centro vazio, ações à direita (Excluir ghost +
 *    Editar primária — botão primário ÚNICO).
 *  - Hero de identidade: avatar + nome + linha-resumo + chips (tipo, status,
 *    segmento).
 *  - Grade 2 colunas (colapsa para 1 no mobile) de cards: Dados, Endereço,
 *    Contatos vinculados, Tickets do cliente, Projetos do cliente.
 *
 * Tokens `var(--theme-*)` (página adapta ao tema). Nenhuma variante `dark:`.
 *
 * Tickets do cliente: `GET /tickets?filters[customerId]=X` — filtro suportado
 * por `TicketQuerySchema.filters.customerId`. Card só renderiza (e só dispara o
 * fetch cross-module) quando o usuário tem `tickets.read` (Cross-Module
 * Permissions, FE CLAUDE.md #15).
 *
 * Projetos do cliente: `GET /projects` HOJE não aceita `clientId` como filtro —
 * o card mostra estado vazio "em breve" documentando a limitação; não varremos
 * todos os projetos do tenant client-side.
 */

import React, { useState } from 'react';
import { TagChip } from '@/shared/components/ui';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Badge,
  Avatar,
  AvatarImage,
  AvatarFallback,
  Breadcrumb,
  Button,
  DeleteConfirmationModal,
  ListPageLoading,
  PageToolbar,
  StatusPill,
} from '@/shared/components/ui';
import type { StatusPillVariant } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { usePermissions } from '@/hooks/usePermissions';
import { useTickets } from '@/modules/helpdesk/tickets/hooks';
import { useProjects } from '@/modules/helpdesk/projects/hooks';

import { CustomerContactsCard, getCustomerTypeBadge } from '../components';
import { useCustomer, useDeleteCustomer } from '../hooks';

/* F62: pills de status/prioridade usam o StatusPill shared (mesma família da listagem). */
const TICKET_PRIORITY_BADGE: Record<string, { variant: StatusPillVariant; label: string }> = {
  urgent: { variant: 'error', label: 'Urgente' },
  high: { variant: 'warning', label: 'Alta' },
  medium: { variant: 'success', label: 'Média' },
  low: { variant: 'success', label: 'Baixa' },
};

/** Sublinha do item de ticket (mockup: "Aberto · alta prioridade"). */
const TICKET_STATUS_LABEL: Record<string, string> = {
  new: 'Novo',
  open: 'Aberto',
  in_progress: 'Em andamento',
  pending: 'Pendente',
  resolved: 'Resolvido',
  closed: 'Fechado',
};

/** Sublinha do item de projeto (mockup: "Em andamento · 6 tarefas"). */
const PROJECT_STATUS_LABEL: Record<string, string> = {
  active: 'Ativo',
  completed: 'Concluído',
  archived: 'Arquivado',
};

/** "Cliente desde jan. de 2025" a partir do `createdAt` (ISO). */
function formatMemberSince(iso?: string): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
}

/* ------------------------------------------------------------------ */
/* Primitivos visuais locais (anatomia do mockup, tokens do tema)      */
/* ------------------------------------------------------------------ */

/** Container de card do detalhe: header (título + meta + ação) + corpo. */
const DetailCard: React.FC<{
  title: string;
  meta?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
  testId?: string;
}> = ({ title, meta, action, children, testId }) => (
  <div
    className="rounded-xl overflow-hidden"
    style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}
    data-testid={testId}
  >
    <div
      className="flex items-center justify-between gap-3 px-4 py-3"
      style={{ borderBottom: '1px solid var(--theme-card-border)' }}
    >
      <h2 className="text-sm font-bold flex items-center gap-1.5 min-w-0" style={{ color: 'var(--theme-card-text)' }}>
        <span className="truncate">{title}</span>
        {meta != null && (
          <span className="text-xs font-medium flex-none" style={{ color: 'var(--theme-muted-text)' }}>
            · {meta}
          </span>
        )}
      </h2>
      {action}
    </div>
    {children}
  </div>
);

/** Linha chave/valor (key column fixa + valor). */
const InfoRow: React.FC<{ label: string; value?: string; divider?: boolean }> = ({ label, value, divider }) => (
  <div
    className="flex gap-2.5 px-4 py-2.5 text-[13px]"
    style={divider ? { borderTop: '1px solid var(--theme-card-border)' } : undefined}
  >
    <span className="flex-none" style={{ color: 'var(--theme-muted-text)', width: 86 }}>
      {label}
    </span>
    <span
      className="min-w-0 font-medium break-words"
      style={{ color: value ? 'var(--theme-card-text)' : 'var(--theme-muted-text)' }}
    >
      {value || '—'}
    </span>
  </div>
);

/** Linha "deal" (ícone colorido + título + subtítulo + trailing). */
const DealRow: React.FC<{
  icon: IconName;
  iconColor: string;
  title: string;
  subtitle?: string;
  trailing?: React.ReactNode;
  divider?: boolean;
  onClick?: () => void;
  testId?: string;
}> = ({ icon, iconColor, title, subtitle, trailing, divider, onClick, testId }) => (
  <div
    className={`flex items-center gap-3 px-4 py-3 ${onClick ? 'cursor-pointer transition-colors' : ''}`}
    style={divider ? { borderTop: '1px solid var(--theme-card-border)' } : undefined}
    onClick={onClick}
    onMouseEnter={onClick ? (e) => (e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)') : undefined}
    onMouseLeave={onClick ? (e) => (e.currentTarget.style.backgroundColor = 'transparent') : undefined}
    data-testid={testId}
  >
    <div
      className="w-9 h-9 rounded-[10px] flex items-center justify-center flex-none"
      style={{ backgroundColor: iconColor, color: '#ffffff' }}
    >
      <Icon name={icon} className="w-4 h-4" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold truncate" style={{ color: 'var(--theme-card-text)' }}>
        {title}
      </p>
      {subtitle && (
        <p className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
          {subtitle}
        </p>
      )}
    </div>
    {trailing != null && <div className="flex-none">{trailing}</div>}
  </div>
);

/* ------------------------------------------------------------------ */
/* Cards de conteúdo                                                   */
/* ------------------------------------------------------------------ */

/** Card "Tickets do cliente" — só renderiza quando `tickets.read` está presente. */
const CustomerTicketsCard: React.FC<{ customerId: string }> = ({ customerId }) => {
  const navigate = useNavigate();
  const { data, isLoading } = useTickets({ filters: { customerId }, limit: 5, sortOrder: 'desc' });
  const items = data?.items ?? [];
  // Mockup: "· 2 abertos" (tickets não resolvidos/fechados entre os carregados)
  const openCount = items.filter((t) => t.status !== 'resolved' && t.status !== 'closed').length;
  // Cores variadas por linha (mockup: quadrados coloridos #8b5cf6/#0369a1/…)
  const TICKET_ICON_COLORS = ['#8b5cf6', '#0369a1', '#0d9488', '#b45309', '#4f46e5'];

  return (
    <DetailCard title="Tickets do cliente" meta={`${openCount} aberto${openCount !== 1 ? 's' : ''}`} testId="customers-tickets-card">
      {isLoading && (
        <div className="px-4 py-4">
          <ListPageLoading size="sm" />
        </div>
      )}
      {!isLoading && items.length === 0 && (
        <p className="text-sm px-4 py-4" style={{ color: 'var(--theme-muted-text)' }}>
          Nenhum ticket vinculado a este cliente.
        </p>
      )}
      {!isLoading &&
        items.map((ticket, index) => {
          const priorityBadge = TICKET_PRIORITY_BADGE[ticket.priority] ?? TICKET_PRIORITY_BADGE.medium!;
          const statusLabel = TICKET_STATUS_LABEL[ticket.status] ?? ticket.status;
          // mockup: "#1042" curto à direita — encurta "TCK-2026-000187" → "#187"
          const shortNum = ticket.ticketNumber
            ? `#${String(parseInt(ticket.ticketNumber.split('-').pop() || '', 10) || ticket.ticketNumber)}`
            : null;
          return (
            <DealRow
              key={ticket.id}
              icon="headphones"
              iconColor={TICKET_ICON_COLORS[index % TICKET_ICON_COLORS.length]}
              title={ticket.title}
              subtitle={`${statusLabel} · ${priorityBadge.label.toLowerCase()} prioridade`}
              trailing={shortNum ? (
                <span className="text-xs tabular-nums" style={{ color: 'var(--theme-muted-text)' }}>{shortNum}</span>
              ) : (
                <StatusPill variant={priorityBadge.variant} label={priorityBadge.label} dot={false} />
              )}
              divider={index > 0}
              onClick={() => navigate(`/helpdesk/tickets/${ticket.id}`)}
              testId={`customers-tickets-item-${ticket.id}`}
            />
          );
        })}
    </DetailCard>
  );
};

/** Card "Projetos do cliente" — lista projetos filtrados por cliente (#069). */
const CustomerProjectsCard: React.FC<{ customerId: string }> = ({ customerId }) => {
  const navigate = useNavigate();
  const { data, isLoading } = useProjects({ clientId: customerId, limit: 5, sortOrder: 'desc' });
  const items = data?.items ?? [];
  const total = data?.total ?? items.length;

  return (
    <DetailCard title="Projetos do cliente" meta={total > 0 ? String(total) : undefined} testId="customers-projects-card">
      {isLoading && (
        <div className="px-4 py-4">
          <ListPageLoading size="sm" />
        </div>
      )}
      {!isLoading && items.length === 0 && (
        <p className="text-sm px-4 py-4" style={{ color: 'var(--theme-muted-text)' }}>
          Nenhum projeto vinculado a este cliente.
        </p>
      )}
      {!isLoading &&
        items.map((project, index) => {
          const statusLabel = PROJECT_STATUS_LABEL[project.status] ?? project.status;
          return (
            <DealRow
              key={project.id}
              icon="layers"
              iconColor="#d97706"
              title={project.name}
              subtitle={`${statusLabel} · ${project.taskCount} ${project.taskCount === 1 ? 'tarefa' : 'tarefas'}`}
              trailing={
                <span className="text-xs tabular-nums" style={{ color: 'var(--theme-muted-text)' }}>
                  {project.progress.pct}%
                </span>
              }
              divider={index > 0}
              onClick={() => navigate(`/helpdesk/projects/${project.id}`)}
              testId={`customers-projects-item-${project.id}`}
            />
          );
        })}
    </DetailCard>
  );
};

export const CustomerDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasModulePermission } = usePermissions();
  const canUpdate = hasModulePermission('customers', 'update');
  const canDelete = hasModulePermission('customers', 'delete');
  const canReadTickets = hasModulePermission('tickets', 'read');
  const canReadProjects = hasModulePermission('projects', 'read');

  const { data: customer, isLoading, error } = useCustomer(id);
  const deleteMutation = useDeleteCustomer();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const handleDelete = () => {
    if (!id) return;
    deleteMutation.mutate(id, {
      onSuccess: () => navigate('/crm/customers'),
    });
  };

  if (isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: 'var(--theme-page-background)' }}
      >
        <ListPageLoading message="Carregando cliente..." />
      </div>
    );
  }

  if (error || !customer || !id) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: 'var(--theme-page-background)' }}
      >
        <div className="text-center">
          <p style={{ color: 'var(--theme-error-color)' }} className="mb-3">
            {(error as Error | undefined)?.message ?? 'Cliente não encontrado'}
          </p>
          <button
            type="button"
            onClick={() => navigate('/crm/customers')}
            className="text-sm hover:underline"
            style={{ color: 'var(--theme-primary)' }}
            data-testid="customers-detail-error-back"
          >
            Voltar para a lista
          </button>
        </div>
      </div>
    );
  }

  const typeBadge = getCustomerTypeBadge(customer.type);
  const memberSince = formatMemberSince(customer.createdAt);
  const subParts = [typeBadge.label, customer.segment, memberSince ? `Cliente desde ${memberSince}` : null].filter(
    Boolean,
  ) as string[];

  return (
    <div
      className="h-full flex flex-col overflow-hidden"
      style={{ backgroundColor: 'var(--theme-page-background)' }}
      data-testid="customers-detail-page"
    >
      {/* Toolbar de página (F23: PageToolbar shared, fundo --theme-header-background) */}
      <PageToolbar
        data-testid="customers-detail-toolbar"
        left={
          /* F25-31: Breadcrumb shared — mobile = legenda condensada sob o título */
          <Breadcrumb
            items={[
              { label: 'CRM', onNavigate: () => navigate('/crm'), testid: 'customers-detail-breadcrumb-crm' },
              { label: 'Clientes', onNavigate: () => navigate('/crm/customers'), testid: 'customers-detail-back' },
              { label: customer.name, testid: 'customers-detail-breadcrumb-current' },
            ]}
            mobileLegend="CRM · Cliente"
            data-testid="customers-detail-breadcrumb"
          />
        }
        right={
          <>
            <div className="hidden sm:flex items-center gap-2 flex-none">
              {canDelete && (
                <Button
                  variant="secondary"
                  icon="trash"
                  collapsibleLabel
                  onClick={() => setIsDeleteOpen(true)}
                  title="Excluir cliente"
                  data-testid="customers-detail-delete"
                >
                  Excluir
                </Button>
              )}
              {canUpdate && (
                <Button
                  variant="primary"
                  icon="edit"
                  collapsibleLabel
                  onClick={() => navigate(`/crm/customers/${id}/edit`)}
                  data-testid="customers-detail-edit"
                >
                  Editar
                </Button>
              )}
            </div>
            {/* Ações mobile: ••• menu (mockup m-ibtn #i-more-v) — vira ActionSheet no mobile */}
            {(canUpdate || canDelete) && (
              <div className="sm:hidden flex-none">
                <CardActionsMenu
                  actions={[
                    canUpdate && { label: 'Editar', icon: 'edit', onClick: () => navigate(`/crm/customers/${id}/edit`) },
                    canDelete && { label: 'Excluir', icon: 'trash', onClick: () => setIsDeleteOpen(true), variant: 'danger' },
                  ].filter(Boolean) as CardAction[]}
                  size="sm"
                />
              </div>
            )}
          </>
        }
      />

      {/* Conteúdo */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
          {/* Hero de identidade */}
          <div
            className="flex items-center gap-4 rounded-xl p-5 mb-4"
            style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}
          >
            {/* mockup: avatar roxo SÓLIDO com INICIAIS (não ícone) — F83: pessoa = squircle */}
            <Avatar className="w-16 h-16 flex-shrink-0" isSquircle>
              <AvatarImage src={customer.picture} alt={customer.name} isSquircle />
              <AvatarFallback
                isSquircle
                style={{ backgroundColor: 'var(--theme-primary)', color: '#ffffff' }}
              >
                <span className="text-xl font-bold">
                  {(customer.name || '?').trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
                </span>
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <h1
                className="text-xl font-bold truncate"
                style={{ color: 'var(--theme-card-text)', letterSpacing: '-0.02em' }}
              >
                {customer.name}
              </h1>
              {subParts.length > 0 && (
                <p className="text-sm mt-0.5 truncate" style={{ color: 'var(--theme-muted-text)' }}>
                  {subParts.join(' · ')}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-2 mt-2.5">
                <Badge variant="secondary">{typeBadge.label}</Badge>
                {/* F68: detalhe alinha ao StatusPill da listagem */}
                <StatusPill
                  variant={customer.status === 'active' ? 'success' : 'neutral'}
                  label={customer.status === 'active' ? 'Ativo' : 'Inativo'}
                  data-testid="customers-detail-status"
                />
                {customer.segment && (
                  <span
                    className="inline-flex items-center h-[22px] px-2.5 rounded-full text-xs font-semibold"
                    style={{ backgroundColor: 'var(--theme-button-ghost-hover)', color: 'var(--theme-card-text)' }}
                  >
                    {customer.segment}
                  </span>
                )}
              </div>
            </div>
            {/* Ações do hero (mockup `.prof-actions`): Editar + WhatsApp (mini-btn 34px) */}
            <div className="flex-none flex flex-col sm:flex-row gap-2 self-start">
              <button
                onClick={() => navigate(`/crm/customers/${id}/edit`)}
                className="inline-flex items-center gap-1.5 h-[34px] px-3 rounded-[9px] text-[13px] font-semibold transition-colors"
                style={{ border: '1px solid var(--theme-card-border)', color: 'var(--theme-card-text)' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                data-testid="customer-hero-edit"
              >
                <Icon name="edit" className="w-4 h-4" />
                <span className="hidden sm:inline">Editar</span>
              </button>
              {customer.phone && (
                <a
                  href={`https://wa.me/${customer.phone.replace(/\D/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 h-[34px] px-3 rounded-[9px] text-[13px] font-semibold transition-colors"
                  style={{ border: '1px solid var(--theme-card-border)', color: 'var(--theme-card-text)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  data-testid="customer-hero-whatsapp"
                >
                  <Icon name="message-circle" className="w-4 h-4" />
                  <span className="hidden sm:inline">WhatsApp</span>
                </a>
              )}
            </div>
          </div>

          {/* Grade de cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
            {/* Dados */}
            <DetailCard title="Dados" testId="customers-data-card">
              <InfoRow label={customer.type === 'PF' ? 'CPF' : 'CNPJ'} value={customer.document} />
              <InfoRow label="E-mail" value={customer.email} divider />
              <InfoRow label="Telefone" value={customer.phone} divider />
              <InfoRow label="Website" value={customer.website} divider />
              <InfoRow label="Segmento" value={customer.segment} divider />
              {customer.domains.length > 0 && (
                <div className="px-4 py-3" style={{ borderTop: '1px solid var(--theme-card-border)' }}>
                  <p className="text-xs mb-1.5" style={{ color: 'var(--theme-muted-text)' }}>
                    Domínios
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {customer.domains.map((domain) => (
                      <span
                        key={domain}
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                        style={{ backgroundColor: 'var(--theme-button-ghost-hover)', color: 'var(--theme-card-text)' }}
                      >
                        {domain}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {customer.tags.length > 0 && (
                <div className="px-4 py-3" style={{ borderTop: '1px solid var(--theme-card-border)' }}>
                  <p className="text-xs mb-1.5" style={{ color: 'var(--theme-muted-text)' }}>
                    Tags
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {customer.tags.map((tag) => (
                      <TagChip
                        key={tag}
                        name={tag}
                        className="text-xs font-medium"
                        style={{ padding: '2px 8px' }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </DetailCard>

            {/* Endereço */}
            <DetailCard title="Endereço" testId="customers-address-card">
              {customer.address ? (
                <>
                  <InfoRow
                    label="Endereço"
                    value={`${customer.address.street}, ${customer.address.number}${customer.address.complement ? ` — ${customer.address.complement}` : ''}`}
                  />
                  <InfoRow label="Bairro" value={customer.address.district} divider />
                  <InfoRow label="Cidade/UF" value={`${customer.address.city}/${customer.address.state}`} divider />
                  <InfoRow label="CEP" value={customer.address.zipCode} divider />
                  <InfoRow label="País" value={customer.address.country} divider />
                </>
              ) : (
                <p className="text-sm px-4 py-4" style={{ color: 'var(--theme-muted-text)' }}>
                  Endereço não cadastrado.
                </p>
              )}
            </DetailCard>

            {/* Contatos vinculados */}
            <CustomerContactsCard customerId={id} contactIds={customer.contacts} canManage={canUpdate} />

            {/* Tickets do cliente — gated por permissão do módulo tickets */}
            {canReadTickets && <CustomerTicketsCard customerId={id} />}

            {/* Projetos do cliente — placeholder até expor clientId em GET /projects */}
            {canReadProjects && <CustomerProjectsCard customerId={id} />}
          </div>
        </div>
      </div>

      <DeleteConfirmationModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleDelete}
        title="Excluir cliente"
        message={`Tem certeza que deseja excluir o cliente "${customer.name}"?`}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
};

CustomerDetailPage.displayName = 'CustomerDetailPage';
